import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Chip, Button as MUIButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { usePageStore } from "@/hooks/usePageStore";
import { useGetSettings } from "@/hooks/useSetting";
import { useGeofenceSearch } from "@/hooks/useGeofences";
import { useGetPrograms } from "@/hooks/usePrograms";
import { useAppTheme } from "@/utils/theme";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import Select from "@/components/Select";
import BasicDatepicker from "@/components/Datepicker";
import SearchBox from "@/components/SearchBox";
import Breadcrumbs from "@/components/Breadcrumbs";
import GenericAlertModal from "@/components/AlertModal";
import { headCellsConfig } from "@/utils/tableConfig";
import {
  GeoFenceAction,
  GeoFenceAddressType,
  GeofenceListItem,
} from "@/domain/geofence.interface";
import { formatInBackofficeLondon } from "@/utils/dateUtils";

type Ordering = {
  pageNumber: number;
  pageSize: number;
  sortOrder: number;
  sortField: string;
};

const GeofencesPage: FC = () => {
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("geofence");
  const { data: settings } = useGetSettings();
  const navigate = useNavigate();
  const theme = useAppTheme();
  const { programs } = useGetPrograms({ sortType: "asc" });

  useEffect(() => {
    setPageTitle(t("pageTitle"));
  }, []);

  const [filters, setFilters] = useState({
    search: "",
    action: "",
    addressType: "",
    programId: "",
    dateFrom: dayjs().subtract(1, "month"),
    dateTo: dayjs(),
  });

  const [sortOrdering, setSortOrdering] = useState<Ordering>({
    pageNumber: 0,
    pageSize: 25,
    sortOrder: -1,
    sortField: "timestamp",
  });

  const [showError, setShowError] = useState(false);

  const {
    data: geofenceData,
    isFetching,
    refetch,
    error,
    isError,
  } = useGeofenceSearch({
    pageNumber: sortOrdering.pageNumber,
    pageSize: sortOrdering.pageSize,
    sortField: sortOrdering.sortField,
    sortOrder: sortOrdering.sortOrder,
    search: filters.search || undefined,
    action: filters.action || undefined,
    addressType: filters.addressType || undefined,
    programId: filters.programId || undefined,
    dateFrom: filters.dateFrom.startOf("day").toISOString(),
    dateTo: filters.dateTo.endOf("day").toISOString(),
  });

  useEffect(() => {
    if (isError) setShowError(true);
  }, [isError]);

  const headCells: HeadCell[] = headCellsConfig.geofence;

  const rows = useMemo(() => {
    if (!geofenceData?.geofences) return [];

    const cellStyle = { fontSize: "14px" };
    const dateFormat =
      (settings?.generalDateFormat as string) || "DD MMM YYYY, hh:mm A";

    return geofenceData.geofences.map((row: GeofenceListItem) => {
      return createDataRow(row._id, {
        participantName: (
          <div style={{ ...cellStyle, minWidth: "160px" }}>
            {row.participantName}
          </div>
        ),
        action: (
          <Chip
            label={t(`action.${row.action}`)}
            size="small"
            color={row.action === GeoFenceAction.ENTER ? "success" : "warning"}
            variant="outlined"
          />
        ),
        addressType: (
          <div style={{ ...cellStyle, textTransform: "capitalize" }}>
            {row.addressType ? t(`addressType.${row.addressType}`) : "-"}
          </div>
        ),
        programName: (
          <div style={{ ...cellStyle, minWidth: "160px" }}>
            {row.programName || "-"}
          </div>
        ),
        timestamp: (
          <div style={{ ...cellStyle, minWidth: "180px" }}>
            {formatInBackofficeLondon(row.timestamp, dateFormat)}
          </div>
        ),
        accuracy: (
          <div style={cellStyle}>
            {row.accuracy ? t(`accuracy.${row.accuracy}`) : "-"}
          </div>
        ),
        source: (
          <Chip
            label={row.source ? (t as (key: string) => string)(`source.${row.source}`) : "-"}
            size="small"
            color={row.source === "sdk_native" ? "info" : "default"}
            variant="outlined"
          />
        ),
        osType: (
          <div style={{ ...cellStyle, textTransform: "capitalize" }}>
            {row.osType || "-"}
          </div>
        ),
      });
    });
  }, [geofenceData, settings?.generalDateFormat, t]);

  const handleActionChange = (value: string | string[]) => {
    setFilters((prev) => ({ ...prev, action: value as string }));
    setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
  };

  const handleAddressTypeChange = (value: string | string[]) => {
    setFilters((prev) => ({ ...prev, addressType: value as string }));
    setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
  };

  const handleProgramChange = (value: string | string[]) => {
    setFilters((prev) => ({ ...prev, programId: value as string }));
    setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
  };

  const handleDateFromChange = (value: Dayjs | null) => {
    if (value) {
      setFilters((prev) => ({ ...prev, dateFrom: value }));
      setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
    }
  };

  const handleDateToChange = (value: Dayjs | null) => {
    if (value) {
      setFilters((prev) => ({ ...prev, dateTo: value }));
      setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
    }
  };

  const handleSearchChange = (value: string) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setSortOrdering((prev) => ({ ...prev, pageNumber: 0 }));
  };

  return (
    <Box>
      <Breadcrumbs
        items={[
          {
            label: t("pageTitle"),
            href: "/geofences",
            isLink: false,
          },
        ]}
      />

      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          mt: 2,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 220 }}>
          <SearchBox
            theme={theme}
            value={filters.search}
            onChange={handleSearchChange}
            placeHolder={t("filters.search")}
          />
        </Box>

        <Select
          id="action-filter"
          label={t("filters.action")}
          value={filters.action}
          handleChange={handleActionChange}
          data={[
            { key: "", value: t("filters.all") },
            { key: GeoFenceAction.ENTER, value: t("action.ENTER") },
            { key: GeoFenceAction.EXIT, value: t("action.EXIT") },
          ]}
          sx={{ minWidth: 160 }}
        />

        <Select
          id="address-type-filter"
          label={t("filters.addressType")}
          value={filters.addressType}
          handleChange={handleAddressTypeChange}
          data={[
            { key: "", value: t("filters.all") },
            { key: GeoFenceAddressType.PRIMARY, value: t("addressType.primary") },
            { key: GeoFenceAddressType.SECONDARY, value: t("addressType.secondary") },
          ]}
          sx={{ minWidth: 160 }}
        />

        <Select
          id="program-filter"
          label={t("filters.program")}
          value={filters.programId}
          handleChange={handleProgramChange}
          data={[
            { key: "", value: t("filters.all") },
            ...(programs || []).map((p: { _id: string; name: string }) => ({
              key: p._id,
              value: p.name,
            })),
          ]}
          sx={{ minWidth: 200 }}
        />

        <BasicDatepicker
          label={t("filters.dateFrom")}
          value={filters.dateFrom}
          onChange={handleDateFromChange}
          maxDate={filters.dateTo}
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": { height: 48, maxHeight: 48 },
            "& input": {
              height: 48,
              maxHeight: 48,
              padding: "12px 14px",
            },
          }}
          disableWrite={true}
        />

        <BasicDatepicker
          label={t("filters.dateTo")}
          value={filters.dateTo}
          onChange={handleDateToChange}
          minDate={filters.dateFrom}
          maxDate={dayjs()}
          sx={{
            minWidth: 180,
            "& .MuiInputBase-root": { height: 48, maxHeight: 48 },
            "& input": {
              height: 48,
              maxHeight: 48,
              padding: "12px 14px",
            },
          }}
          disableWrite={true}
        />

        <MUIButton
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={() => refetch()}
          disabled={isFetching}
          sx={{ height: 48, minWidth: 120 }}
        >
          {t("filters.searchButton")}
        </MUIButton>
      </Box>

      <DataTable
        data={rows}
        headCells={headCells}
        order={sortOrdering.sortOrder === 1 ? "asc" : "desc"}
        orderBy={sortOrdering.sortField}
        rowsPerPage={sortOrdering.pageSize}
        page={sortOrdering.pageNumber}
        total={geofenceData?.totalCount || 0}
        isLoading={isFetching}
        onChangePage={(event, newPage) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: newPage,
          }));
        }}
        onChangeSize={(event) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: 0,
            pageSize: Number(event.target.value),
          }));
        }}
        onChangeSort={(order, orderBy) => {
          setSortOrdering((prev) => ({
            ...prev,
            pageNumber: 0,
            sortField: orderBy as string,
            sortOrder: order === "asc" ? 1 : -1,
          }));
        }}
        onRowClick={(event, rowId) => {
          navigate(`/geofences/${rowId}`);
        }}
        messageEmptyData={t("emptyMessage")}
      />

      {showError && (
        <GenericAlertModal
          title={t("error.title")}
          description={
            (error as { message?: string })?.message ||
            t("error.defaultMessage")
          }
          submitText={t("error.close")}
          onClick={() => setShowError(false)}
          onCancel={() => setShowError(false)}
          showFotter={true}
        />
      )}
    </Box>
  );
};

export default GeofencesPage;
